/**
 * Caricamento dati giocatori: import Excel/online, hydration statistiche e aggiornamento stato
 * (estratto da FantAPP via mixin sul prototype).
 * Tutte le funzioni usano `this` (l'istanza FantAPP) esattamente come prima.
 */
import { log } from "../utils/logger.js";
import { filtersManager } from "../features/filters/index.js";
import { SessionPersistence } from "../services/SessionPersistence.js";
import { CURRENT_SEASON_CODE } from "../services/PlayerStatsService.js";
import { sanitizePlayerListNames, normalizePlayerLookupKey, normalizePlayerNameKey } from "../utils/decodeHtmlEntities.js";

function clearSeasonMatchStats(player) {
  return {
    ...player,
    fm_score: null,
    matches: null,
    goals: null,
    assists: null,
    yellow_cards: null,
    red_cards: null,
    has_season_stats: false,
  };
}

function seasonStatNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** FantaMedia stagione: 0 o assente → null (UI «-»), mai fallback su FVM */
function seasonFmScore(value) {
  const n = seasonStatNumber(value);
  if (n === null || n === 0) return null;
  return n;
}

export const playerDataMethods = {
  /**
   * Processo file Excel
   */
  async processExcelFile(file) {
    try {
      log.info("File: Processamento iniziato", file.name);
      log.time("File: Processamento");

      const result = await this.state.playerStatsService.processExcelFile(file);

      if (result.success) {
        await this.updatePlayersData(result.data.players);
        this._afterPlayersLoaded(result.data.players.length, result.data.withStats);
      } else {
        this.showFileError(result.error);
      }

      log.timeEnd("File: Processamento");
    } catch (error) {
      log.error("File: Errore processamento", error);
      this.showFileError(error.message);
    }
  },

  /**
   * Passi comuni dopo che i giocatori sono stati caricati (da Excel o da online):
   * messaggio di successo, abilita "Inizia asta"; resta su setup finché l'utente non preme il CTA.
   */
  _afterPlayersLoaded(count, withStats) {
    this.showFileSuccess(count, withStats);
    if (typeof this._renderUnifiedListBlock === 'function') {
      this._renderUnifiedListBlock();
    }
    if (typeof this._updateUnifiedStartButtonState === 'function') {
      this._updateUnifiedStartButtonState();
    }
    SessionPersistence.setView('auction', 'setup');
  },

  /**
   * Prova a scaricare le quotazioni più recenti da Fantacalcio.it.
   * Non tocca il DOM: il chiamante gestisce lo stato del proprio bottone.
   */
  async loadQuotazioniOnline() {
    try {
      const result = await this.state.playerStatsService.loadPlayersFromOnline();
      if (result.success) {
        await this.updatePlayersData(result.data.players);
        this._afterPlayersLoaded(result.data.players.length, result.data.withStats);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (error) {
      log.error("Online: errore aggiornamento quotazioni", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Aggiorna dati giocatori con hydration automatica da PocketBase
   */
  async updatePlayersData(players) {
    this.state.allPlayers = players || [];

    // Hydration delle statistiche da PocketBase se utente autenticato
    if (this.state.userSession && this.state.userSession.isAuthenticated()) {
      try {
        log.info("Hydration: Caricamento statistiche da PocketBase...");
        const hydratedPlayers = await this.hydratePlayersWithStats(this.state.allPlayers);
        this.state.allPlayers = hydratedPlayers;
        log.info("Hydration: Completata", hydratedPlayers.length, "giocatori con stats");
      } catch (error) {
        log.warn("Hydration: Errore caricamento statistiche, continuo senza", error);
      }
    }

    // Riconcilia i target dello Studio con la lista appena caricata.
    if (typeof this.runStudioReconciliation === 'function') {
      try {
        await this.runStudioReconciliation();
      } catch (err) {
        log.warn('Studio: riconciliazione fallita, continuo', err);
      }
    }

    this.state.playersHydrated = this.state.allPlayers; // Per compatibilità con i test

    log.info("Dati: Giocatori aggiornati", this.state.allPlayers.length);

    // Aggiorna filtri manager
    filtersManager.setPlayers(this.state.allPlayers);

    // *** FIX: Forza applicazione filtri per inviare dati al TableManager ***
    // Questo assicura che il TableManager riceva subito i dati filtrati
    setTimeout(() => {
      filtersManager.applyFilters();
      console.log('[main.js] 🔄 Filtri applicati dopo setPlayers per sincronizzazione TableManager');
    }, 50);

    // *** RIMOSSO: Non aggiornare TableManager direttamente ***
    // Il TableManager riceve i dati SOLO attraverso i filtri via Events.TABLE_UPDATE
    // if (this.tableManager && this.state.allPlayers.length > 0) {
    //   log.info("TableManager: Aggiornamento dati...");
    //   this.tableManager.updatePlayers(this.state.allPlayers);
    // }


    // Copia di sessione: sopravvive al refresh tab (dev=localStorage, prod=sessionStorage).
    if (this.state.allPlayers.length > 0) {
      SessionPersistence.savePlayers(this.state.allPlayers);
      log.info("Persistenza: Giocatori salvati in sessione");
    }

    // Archivio duraturo «ultima lista» su PocketBase (per ricarico dopo login/gate asta).
    this.savePlayersList(this.state.allPlayers);
  },

  /**
   * Hydrata giocatori con statistiche da PocketBase
   */
  async hydratePlayersWithStats(players) {
    try {
      // Nomi da Fantacalcio possono arrivare ancora encoded (session/cache):
      // senza decode il match su player_stats fallisce (es. Kon&#xE8; M. ≠ Konè M.).
      const sanitized = sanitizePlayerListNames(players);

      // Solo ultima stagione completa (25/26) per Partite/Gol/Assist/Gialli/Rossi
      const statsResult = await this.state.playerStatsService.loadPlayerStats(CURRENT_SEASON_CODE);

      if (!statsResult.success || !statsResult.data) {
        log.info("Hydration: Nessuna statistica trovata in PocketBase");
        return sanitized.map(clearSeasonMatchStats);
      }

      const byNameTeam = new Map();
      const byName = new Map();
      statsResult.data.forEach(stat => {
        byNameTeam.set(normalizePlayerLookupKey(stat.player_name, stat.team_name), stat);
        const nameKey = normalizePlayerNameKey(stat.player_name);
        if (!byName.has(nameKey)) byName.set(nameKey, []);
        byName.get(nameKey).push(stat);
      });

      log.info("Hydration: Statistiche caricate dal database", byNameTeam.size);

      const lookupSeasonStats = (player) => {
        const direct = byNameTeam.get(normalizePlayerLookupKey(player.name, player.team));
        if (direct) return direct;
        const sameName = byName.get(normalizePlayerNameKey(player.name));
        if (sameName && sameName.length === 1) return sameName[0];
        return null;
      };

      const hydratedPlayers = sanitized.map(player => {
        const dbStats = lookupSeasonStats(player);

        if (dbStats) {
          return {
            ...player,
            fm_score: seasonFmScore(dbStats.fm_score),
            matches: seasonStatNumber(dbStats.matches),
            goals: seasonStatNumber(dbStats.goals),
            assists: seasonStatNumber(dbStats.assists),
            yellow_cards: seasonStatNumber(dbStats.yellow_cards),
            red_cards: seasonStatNumber(dbStats.red_cards),
            has_season_stats: true,
          };
        }
        return clearSeasonMatchStats(player);
      });

      const withStats = hydratedPlayers.filter(p => p.has_season_stats).length;
      log.info(`Hydration: ${withStats}/${hydratedPlayers.length} giocatori con statistiche ${CURRENT_SEASON_CODE}`);

      return hydratedPlayers;
    } catch (error) {
      log.error("Hydration: Errore durante hydration", error);
      return sanitizePlayerListNames(players).map(clearSeasonMatchStats);
    }
  },
};
